import { useEffect, useRef } from "react";
import "./GridPulses.css";

// =============================================================================
// 🎛️  TUNABLE SETTINGS — edit these numbers to change how the lights look/feel.
// (After editing, just save; the running site reloads automatically.)
// =============================================================================
const CONFIG = {
  gridSize: 46, // spacing between grid lines, in pixels. MUST match the grid in index.css (background-size).

  desktopCount: 22, // how many lights are on screen at once on a big screen. Higher = busier.
  mobileCount: 12, // how many lights on a phone-sized screen. Lower = lighter on battery.
  mobileBreakpoint: 700, // screens narrower than this (px) use mobileCount.

  minSpeed: 45, // slowest a light can travel (pixels per second). Lower = slower drift.
  speedRange: 80, // extra random speed on top of minSpeed. So speed = 45 to (45+80)=125 px/s.

  goldChance: 0.18, // chance (0–1) a light is gold instead of cyan. 0 = all cyan, 1 = all gold.
  horizontalChance: 0.5, // chance (0–1) a light travels sideways vs. up/down. 0.5 = even mix.

  tailLength: 90, // length of the glowing tail behind each light, in pixels. Bigger = longer streak.
  lineWidth: 1, // thickness of the light's trail, in pixels.
  tailAlpha: 0.2, // brightness of the tail (0–1). Higher = more visible streak.

  headRadius: 0, // size of the bright dot at the front of each light, in pixels. 0 = no dot.
  headAlpha: 0.95, // brightness of that dot (0–1).
  glowBlur: 8, // size of the soft glow around the dot, in pixels. Higher = dreamier halo.

  cyan: "53, 214, 231", // the cyan color, as "R, G, B". Change to recolor the cyan lights.
  gold: "245, 179, 60", // the gold color, as "R, G, B". Change to recolor the gold lights.
};
// =============================================================================

/**
 * Ambient background: small glowing "data" lights travel along the grid lines,
 * with a fading tail, making the grid feel alive. Rendered on a fixed canvas
 * behind all content. Respects prefers-reduced-motion (renders nothing).
 */
export default function GridPulses() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return undefined; // e.g. jsdom / no canvas support

    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    const C = CONFIG;
    let width = 0;
    let height = 0;
    let dpr = 1;
    const pulses = [];

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2); // caps pixel density at 2x for performance
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // How many lights to keep alive (fewer on small screens)
    const max = () => (width < C.mobileBreakpoint ? C.mobileCount : C.desktopCount);

    const spawn = (seed) => {
      const horizontal = Math.random() < C.horizontalChance;
      const color = Math.random() < C.goldChance ? C.gold : C.cyan;
      const speed = C.minSpeed + Math.random() * C.speedRange;
      const dir = Math.random() < 0.5 ? 1 : -1; // travel direction: forward or backward
      if (horizontal) {
        const y = Math.round((Math.random() * height) / C.gridSize) * C.gridSize; // snap to a grid line
        const x = seed ? Math.random() * width : dir > 0 ? -30 : width + 30; // seed = start mid-screen; else start off-edge
        pulses.push({ x, y, vx: dir * speed, vy: 0, color });
      } else {
        const x = Math.round((Math.random() * width) / C.gridSize) * C.gridSize; // snap to a grid line
        const y = seed ? Math.random() * height : dir > 0 ? -30 : height + 30;
        pulses.push({ x, y, vx: 0, vy: dir * speed, color });
      }
    };

    resize();
    for (let i = 0; i < max(); i++) spawn(true); // pre-fill the screen so it isn't empty at first
    window.addEventListener("resize", resize);

    let last = performance.now();
    let raf;
    const tick = (now) => {
      const dt = Math.min(50, now - last) / 1000; // seconds since last frame (capped so tab-switches don't jump)
      last = now;
      ctx.clearRect(0, 0, width, height); // clear every frame (keeps canvas transparent)

      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        // Remove a light once it fully leaves the screen
        if (p.x < -60 || p.x > width + 60 || p.y < -60 || p.y > height + 60) {
          pulses.splice(i, 1);
          continue;
        }

        const speed = Math.hypot(p.vx, p.vy) || 1;
        const ux = p.vx / speed; // unit direction x
        const uy = p.vy / speed; // unit direction y
        const tx = p.x - ux * C.tailLength; // tail start x
        const ty = p.y - uy * C.tailLength; // tail start y

        // The fading tail
        const grad = ctx.createLinearGradient(tx, ty, p.x, p.y);
        grad.addColorStop(0, `rgba(${p.color}, 0)`);
        grad.addColorStop(1, `rgba(${p.color}, ${C.tailAlpha})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = C.lineWidth;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();

        // The bright glowing head (skipped if headRadius is 0)
        if (C.headRadius > 0) {
          ctx.fillStyle = `rgba(${p.color}, ${C.headAlpha})`;
          ctx.shadowColor = `rgba(${p.color}, 0.9)`;
          ctx.shadowBlur = C.glowBlur;
          ctx.beginPath();
          ctx.arc(p.x, p.y, C.headRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      }

      while (pulses.length < max()) spawn(false); // top up as lights leave the screen
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="grid-pulses" aria-hidden="true" />;
}
